import { observer } from 'mobx-react-lite'
import type { IconName } from '@/components/Icon'
import { SkeletonList } from '@/components/Skeleton'
import type {
  LegendEditorViewModel,
  OptionsTab,
} from '@/features/legends/LegendEditor/LegendEditorViewModel'
import { EditorPage } from '@/features/legends/components/EditorPage'
import {
  SidebarMenu,
  SidebarMenuItem,
} from '@/features/legends/components/SidebarMenu'
import { firstError } from '@/features/legends/editor/validation'
import { GeneralOptionsContainer } from '@/features/legends/options/components/GeneralOptionsContainer'
import { InfoOptionsContainer } from '@/features/legends/options/components/InfoOptionsContainer'
import { OptionsCardSkeleton } from '@/features/legends/options/components/OptionsCard'
import { SidebarLayout } from '@/features/legends/components/SidebarLayout'
import { RoutingTabContainer } from '@/features/legends/routing/components/RoutingTabContainer'
import { TransferCardContainer } from '@/features/legends/transfer/components/TransferCard/TransferCardContainer'

export interface OptionsTabContainerProps {
  vm: LegendEditorViewModel
}

const testId = 'legend-options'

const pages: { tab: OptionsTab; title: string; iconName: IconName }[] = [
  { tab: 'General', title: 'General', iconName: 'sliders' },
  { tab: 'Routing', title: 'Routing', iconName: 'route' },
  { tab: 'Info', title: 'Info', iconName: 'info' },
  { tab: 'Transfer', title: 'Import / Export', iconName: 'download' },
]

/**
 * The options pages, with the selected one.
 */
export const OptionsTabContainer = observer(function OptionsTabContainer({
  vm,
}: OptionsTabContainerProps) {
  return (
    <SidebarLayout
      sidebar={<OptionsSidebarContainer vm={vm} />}
      testId={testId}
    >
      <OptionsPageContainer vm={vm} />
    </SidebarLayout>
  )
})

/**
 * The options pages while the legend loads. The pages can be picked already.
 */
export const OptionsTabSkeletonContainer = observer(
  function OptionsTabSkeletonContainer({ vm }: OptionsTabContainerProps) {
    return (
      <SidebarLayout
        sidebar={<OptionsSidebarContainer vm={vm} isSkeleton />}
        testId={testId}
      >
        <EditorPage>
          <SkeletonList count={2}>
            <OptionsCardSkeleton />
          </SkeletonList>
        </EditorPage>
      </SidebarLayout>
    )
  },
)

interface OptionsSidebarContainerProps extends OptionsTabContainerProps {
  /**
   * Whether we're rendering before the legend has loaded. Errors are left out
   * until then.
   */
  isSkeleton?: boolean
}

const OptionsSidebarContainer = observer(function OptionsSidebarContainer({
  vm,
  isSkeleton,
}: OptionsSidebarContainerProps) {
  return (
    <SidebarMenu title="Options">
      {pages.map(({ tab, title, iconName }) => {
        const validation = isSkeleton
          ? undefined
          : vm.tabValidations[tab]?.validation
        return (
          <SidebarMenuItem
            key={tab}
            title={title}
            iconName={iconName}
            isSelected={vm.currentTab === tab}
            error={validation && firstError(validation)}
            onPress={() => vm.setTab(tab)}
            testId={`${testId}-${tab.toLowerCase()}`}
          />
        )
      })}
    </SidebarMenu>
  )
})

const OptionsPageContainer = observer(function OptionsPageContainer({
  vm,
}: OptionsTabContainerProps) {
  switch (vm.currentTab) {
    case 'Routing':
      return <RoutingTabContainer vm={vm} />
    case 'Info':
      return <InfoOptionsContainer vm={vm.infoVm} />
    case 'Transfer':
      return (
        <EditorPage testId={`${testId}-transfer-page`}>
          <TransferCardContainer vm={vm.transferVm} />
        </EditorPage>
      )
    default:
      return (
        <EditorPage testId={`${testId}-general-page`}>
          <GeneralOptionsContainer vm={vm} />
        </EditorPage>
      )
  }
})
